import { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { messages } from './messages';
import { CardProps } from '@designsystem/stories/03-molecules/Card';
import { Listing } from '@designsystem/stories/04-organisms/Listing';
import {
  Sidepanel,
  SidepanelTabProps,
} from '@designsystem/stories/04-organisms/Sidepanel';
import { SidepanelContext } from '@designsystem/stories/04-organisms/SidepanelContext';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { isPlainClick } from '@fhnw/helpers/Url/isPlainClick';
import { SidepanelContent } from '../../SidepanelContent';

export interface BlockDataProps extends BlockWrapperDataProps {
  specializations?: Content[];
}

interface ViewProps {
  data: BlockDataProps;
  content: Content;
  isEdit?: boolean;
  block: string;
  onChangeBlock: (blockId: string, newData: any) => void;
}

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };
  const [open, doOpen] = useContext<any>(SidepanelContext);
  const [tab, setTab] = useState(0);
  const id = 'sidepanel-' + props.block;

  let updated = false;
  if (!data?.block_title) {
    data.block_title = intl.formatMessage(messages.Title);
    updated = true;
  }

  useEffect(() => {
    if (updated && props?.isEdit && props.onChangeBlock) {
      props.onChangeBlock(props.block, data);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let specializations = data?.specializations as NormalizableContent[];
  if (!specializations || !specializations.length) {
    specializations = content?.specializations as NormalizableContent[];
  }

  const items: CardProps[] = [];
  const panels: SidepanelTabProps[] = [];

  (specializations || []).forEach((specialization) => {
    if (!specialization) {
      return;
    }

    const i = items.length;
    const panelId = `${id}-tab-${i}`;
    const item = transformToCard(specialization, intl, 'panel');
    const active = open === id && tab === i;

    item.onClick = (e: MouseEvent) => {
      if (!isPlainClick(e)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      doOpen(id);
      setTab(i);
    };
    item.linkClassName = 'ga-panel-vertiefungsrichtung';
    item['aria-haspopup'] = 'true';
    item['aria-controls'] = panelId;
    item['aria-expanded'] = active ? 'true' : 'false';

    items.push(item);
    panels.push({
      label: specialization?.short_title || (item.title as string),
      children: (
        <SidepanelContent
          active={open === id && tab === i}
          content={specialization as Content}
        />
      ),
    });
  });

  if (!props?.isEdit && !items.length) {
    return null;
  }

  return (
    <BlockWrapper
      isEdit={props?.isEdit}
      block={props?.block}
      onChangeBlock={props?.onChangeBlock}
      data={data}
      factory={Factory}
    >
      <Sidepanel
        id={id}
        currentTab={tab}
        onTabChange={setTab}
        tabs={panels}
        close={intl.formatMessage(messages.Close)}
      />
      <Listing type="panel" items={items} />
    </BlockWrapper>
  );
};

export default View;
